
import React, { useState, useEffect } from 'react';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../../src/firebase';
import { ShieldCheckIcon, ExclamationTriangleIcon, EyeIcon, EyeSlashIcon, CheckIcon } from '../Icons';

interface ResetPasswordProps {
    oobCode: string;
    onComplete: () => void;
}

export const ResetPassword: React.FC<ResetPasswordProps> = ({ oobCode, onComplete }) => {
    const [newPassword, setNewPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [email, setEmail] = useState<string | null>(null);
    const [isVerifying, setIsVerifying] = useState(true);

    // Validation states
    const passLength = newPassword.length >= 10;
    const hasNumber = /\d/.test(newPassword);
    const hasUpper = /[A-Z]/.test(newPassword);
    const isStrong = passLength && hasNumber && hasUpper;

    useEffect(() => {
        const verifyCode = async () => {
            try {
                const userEmail = await verifyPasswordResetCode(auth, oobCode);
                setEmail(userEmail);
            } catch (err: any) {
                console.error("Verification error:", err);
                setError("Эта ссылка недействительна или срок её действия истек.");
            } finally {
                setIsVerifying(false);
            }
        };
        verifyCode();
    }, [oobCode]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isStrong) return;

        setLoading(true);
        setError(null);
        try {
            await confirmPasswordReset(auth, oobCode, newPassword);
            onComplete();
        } catch (err: any) {
            console.error("Password reset error:", err);
            setError(err.message || "Ошибка при смене пароля. Попробуйте еще раз.");
        } finally {
            setLoading(false);
        }
    };

    if (isVerifying) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4 font-sans">
            <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-gray-100">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <ShieldCheckIcon className="w-10 h-10 text-blue-600" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">Сброс пароля</h1>
                    {email && (
                        <p className="text-gray-500 mt-2">
                            для аккаунта <span className="font-semibold text-gray-700">{email}</span>
                        </p>
                    )}
                </div>

                {error ? (
                    <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 flex items-start gap-3">
                        <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <p className="text-sm">{error}</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="relative">
                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 ml-1">
                                Новый пароль
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Придумайте надежный пароль"
                                    className={`w-full px-4 py-3 bg-gray-50 border rounded-xl outline-none transition-all ${newPassword ? (isStrong ? 'border-green-200 focus:ring-2 focus:ring-green-500/20' : 'border-amber-200 focus:ring-2 focus:ring-amber-500/20') : 'border-gray-200 focus:ring-2 focus:ring-blue-500/20'
                                        }`}
                                    required
                                    autoFocus
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        {/* Strength Guidelines */}
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-2">
                            <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center justify-between">
                                <span>Требования к надежности:</span>
                                <span className={isStrong ? 'text-green-600' : 'text-amber-600'}>
                                    {isStrong ? 'Надежный' : (newPassword.length > 0 ? 'Слабый' : 'Ожидание')}
                                </span>
                            </div>

                            <div className="flex items-center gap-2 text-xs">
                                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passLength ? 'bg-green-100 text-green-600 font-bold' : 'bg-white border text-transparent'}`}>
                                    {passLength && <CheckIcon className="w-2.5 h-2.5" />}
                                </div>
                                <span className={passLength ? 'text-gray-700' : 'text-gray-400'}>Минимум 10 символов</span>
                            </div>

                            <div className="flex items-center gap-2 text-xs">
                                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${hasNumber ? 'bg-green-100 text-green-600 font-bold' : 'bg-white border text-transparent'}`}>
                                    {hasNumber && <CheckIcon className="w-2.5 h-2.5" />}
                                </div>
                                <span className={hasNumber ? 'text-gray-700' : 'text-gray-400'}>Хотя бы одна цифра</span>
                            </div>

                            <div className="flex items-center gap-2 text-xs">
                                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${hasUpper ? 'bg-green-100 text-green-600 font-bold' : 'bg-white border text-transparent'}`}>
                                    {hasUpper && <CheckIcon className="w-2.5 h-2.5" />}
                                </div>
                                <span className={hasUpper ? 'text-gray-700' : 'text-gray-400'}>Хотя бы одна заглавная буква</span>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !isStrong}
                            className={`w-full py-4 rounded-xl font-bold text-sm uppercase tracking-wider transition-all shadow-lg ${isStrong
                                    ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-500/30'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                                }`}
                        >
                            {loading ? 'Смена пароля...' : 'Сохранить и войти'}
                        </button>

                        <p className="text-[11px] text-gray-400 text-center leading-relaxed">
                            Надежный пароль необходим для безопасной работы системы сквозного шифрования (E2EE).
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
};
